import { useState, useEffect } from "react";

// ── Icons ──────────────────────────────────────────────────────────────────────

const CardIcon = () => (
  <svg width="16" height="16" fill="none" stroke="#635bff" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);

const BankIcon = () => (
  <svg width="16" height="16" fill="none" stroke="#4ade80" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18M9 21V9" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="16" height="16" fill="none" stroke="#a78bfa" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const UserIcon = () => (
  <svg width="16" height="16" fill="none" stroke="#fb923c" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
    <polyline points="1,3.5 3.5,6 8,1" stroke="#4BA5EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockIcon = () => (
  <svg width="15" height="15" fill="none" stroke="#6b7280" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
    <path d="M1 1l4 4 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── Types ──────────────────────────────────────────────────────────────────────

type ModalState = "idle" | "confirm" | "loading" | "success" | "error";
type AccountStatus = "checking" | "complete" | "pending" | "incomplete" | "none";

// ── Sub-components ─────────────────────────────────────────────────────────────

interface HowItem {
  icon: React.ReactNode;
  bg: string;
  title: string;
  desc: string;
}

const howItems: HowItem[] = [
  { icon: <CardIcon />, bg: "rgba(99,91,255,0.15)", title: "Connect your account", desc: "Link your Stripe account in a few simple steps." },
  { icon: <BankIcon />, bg: "rgba(74,222,128,0.12)", title: "Get paid directly", desc: "Payments from your customers go directly to your Stripe account." },
  { icon: <ShieldIcon />, bg: "rgba(167,139,250,0.15)", title: "We don't hold funds", desc: "OceSha does not store or manage your money in any way." },
  { icon: <UserIcon />, bg: "rgba(249,115,22,0.15)", title: "You're in control", desc: "You're responsible for refunds, disputes, and customer transactions." },
];

// ── Styles (inline object map) ─────────────────────────────────────────────────

const s = {
  page: {
    background: "transparent",
    paddingRight:25, 
    paddingLeft:25,
    color: "#fff",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  } as React.CSSProperties,

  title: { fontSize: 22, fontWeight: 700, marginBottom: 4 } as React.CSSProperties,
  subtitle: { fontSize: 13, color: "#7c8298", marginBottom: 24 } as React.CSSProperties,

  card: (extra?: React.CSSProperties): React.CSSProperties => ({
    background: "#12131c",
    border: "1px solid #22243a",
    borderRadius: 10,
    ...extra,
  }),

  howBox: {
    background: "#12131c",
    border: "1px solid #22243a",
    borderRadius: 10,
    padding: "18px 20px",
    marginBottom: 16,
  } as React.CSSProperties,

  howTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#cdd1e0",
    marginBottom: 14,
    display: "flex",
    alignItems: "center",
    gap: 8,
  } as React.CSSProperties,

  howGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 12,
  } as React.CSSProperties,

  howIconWrap: (bg: string): React.CSSProperties => ({
    width: 34, height: 34, borderRadius: 8,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: bg, marginBottom: 8,
  }),

  howItemTitle: { fontSize: 12, fontWeight: 600, color: "#cdd1e0", marginBottom: 4 } as React.CSSProperties,
  howItemDesc: { fontSize: 11, color: "#6b7280", lineHeight: 1.5 } as React.CSSProperties,
};

// ── Status banner styles ───────────────────────────────────────────────────────

const statusBanner = {
  base: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    border: "1px solid",
    borderRadius: 10,
    padding: "14px 16px",
    marginBottom: 16,
  } as React.CSSProperties,
};

const spinner: React.CSSProperties = {
  width: 16, height: 16, flexShrink: 0,
  border: "2px solid #22243a", borderTopColor: "#4f6ef7",
  borderRadius: "50%", animation: "spin 0.8s linear infinite",
  marginTop: 2,
};

// ── Main Component ─────────────────────────────────────────────────────────────

export default function AddPayment() {
  const [currency, setCurrency] = useState<string>("USD");
  const [modalState, setModalState] = useState<ModalState>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [accountStatus, setAccountStatus] = useState<AccountStatus>("checking");
  const [currentlyDue, setCurrentlyDue] = useState<string[]>([]);

  const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
  const user_email = localStorage.getItem("user_email") || "";

  // Fetch account status on mount
  useEffect(() => {
    if (!user_email) {
      setAccountStatus("none");
      return;
    }
    fetch(`${apiUrl}/api/subscription/account-status?email=${encodeURIComponent(user_email)}`)
      .then((r) => r.json())
      .then((data) => {
        setAccountStatus(data.status || "none");
        setCurrentlyDue(data.details?.currently_due || []);
      })
      .catch(() => setAccountStatus("none"));
  }, []);

  const openModal = () => setModalState("confirm");
  const closeModal = () => setModalState("idle");

const connectStripe = async () => {
  setModalState("loading");
  setErrorMsg("");

  const user_email = localStorage.getItem("user_email") || "";

  if (!user_email) {
    setErrorMsg("User email not found. Please login again.");
    setModalState("error");
    return;
  }

  try {
    // Build return_url with email so connect_success page can verify the account
    const baseUrl = window.location.origin;
    const return_url = `${baseUrl}/connect-success?email=${encodeURIComponent(user_email)}`;
    const refresh_url = `${baseUrl}/connect-success?email=${encodeURIComponent(user_email)}`;

    const response = await fetch(`${process.env.REACT_APP_SERVER_ENDPOINT}/api/subscription/connect-stripe`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: user_email,
        return_url,
        refresh_url,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to connect");
    }

    if (data.url) {
      window.location.href = data.url;
      return;
    }

    setModalState("success");

  } catch (e: any) {
    setErrorMsg(e.message || "Something went wrong");
    setModalState("error");
  }
};

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.title}>Add payment method</div>
      <div style={s.subtitle}>Connect a payment account to start receiving payments for your courses.</div>

      {/* ── Account Status Banner ── */}
      {accountStatus === "checking" && (
        <div style={{ ...statusBanner.base, borderColor: "#2a2c3e", background: "#1a1b28" }}>
          <div style={spinner} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <span style={{ fontSize: 13, color: "#7c8298" }}>Checking your Stripe account status…</span>
        </div>
      )}
      {accountStatus === "complete" && (
        <div style={{ ...statusBanner.base, borderColor: "#16a34a", background: "rgba(22,163,74,0.08)" }}>
          <span style={{ fontSize: 18 }}>✅</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#4ade80" }}>Stripe Account Connected</div>
            <div style={{ fontSize: 12, color: "#7c8298", marginTop: 2 }}>You're all set — payments and payouts are enabled.</div>
          </div>
        </div>
      )}
      {accountStatus === "pending" && (
        <div style={{ ...statusBanner.base, borderColor: "#ca8a04", background: "rgba(202,138,4,0.08)" }}>
          <span style={{ fontSize: 18 }}>⏳</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#facc15" }}>Verification in Progress</div>
            <div style={{ fontSize: 12, color: "#7c8298", marginTop: 2 }}>Stripe is reviewing your details. Usually takes a few minutes.</div>
            {currentlyDue.length > 0 && (
              <div style={{ fontSize: 12, color: "#f59e0b", marginTop: 4 }}>
                Still needed: {currentlyDue.join(", ")}
              </div>
            )}
          </div>
        </div>
      )}
      {accountStatus === "incomplete" && (
        <div style={{ ...statusBanner.base, borderColor: "#dc2626", background: "rgba(220,38,38,0.08)" }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#f87171" }}>Stripe Connect Incomplete</div>
            <div style={{ fontSize: 12, color: "#7c8298", marginTop: 2 }}>Finish connecting your Stripe account below to start receiving payments.</div>
          </div>
        </div>
      )}

      {/* How payments work */}
      <div style={s.howBox}>
        <div style={s.howTitle}>
          <span style={{ color: "#7c8298", display: "flex" }}><InfoIcon /></span>
          How payments work
        </div>
        <div style={s.howGrid}>
          {howItems.map((item) => (
            <div key={item.title}>
              <div style={s.howIconWrap(item.bg)}>{item.icon}</div>
              <div style={s.howItemTitle}>{item.title}</div>
              <div style={s.howItemDesc}>{item.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Main card */}
      <div style={s.card({ overflow: "hidden" })}>
        <div style={{ padding: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>

            {/* Left */}
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              {/* Radio */}
              <div style={{
                width: 18, height: 18, borderRadius: "50%", border: "2px solid #6366f1",
                display: "flex", alignItems: "center", justifyContent: "center", marginTop: 2, flexShrink: 0,
              }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#6366f1" }} />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {/* Stripe name row */}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{
                    background: "#1a1b28", border: "1px solid #2a2c3e", borderRadius: 6,
                    padding: "5px 8px", fontSize: 12, fontWeight: 700, color: "#635bff", letterSpacing: -0.3,
                  }}>
                    stripe
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>Stripe</span>
                </div>

                {/* Check list */}
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 5 }}>
                  {[
                    "Fast payouts (typically within 1 business day)",
                    "Secure and trusted payment processing",
                    "Available in multiple countries and currencies",
                  ].map((text) => (
                    <li key={text} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#9aa0b4" }}>
                      <div style={{
                        width: 16, height: 16, borderRadius: "50%", border: "1.5px solid #4BA5EB",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        <CheckCircleIcon />
                      </div>
                      {text}
                    </li>
                  ))}
                </ul>

                {/* Security box */}
                <div style={{
                  background: "#1a1b28", border: "1px solid #22243a", borderRadius: 8,
                  padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12, marginTop: 4, width: 850,
                }}>
                  <div style={{
                    width: 34, height: 34, background: "#12131c", border: "1px solid #22243a",
                    borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <LockIcon />
                  </div>
                  <div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "#cdd1e0", marginBottom: 3 }}>Secure &amp; private</div>
                    <div style={{ fontSize: 11.5, color: "#6b7280", lineHeight: 1.5 }}>
                      You will be redirected to Stripe to securely connect your account.<br />
                      We never access your login details or banking information.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Currency select */}
            <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                style={{
                  background: "#1b1d2a", border: "1px solid #2a2c3e", borderRadius: 6,
                  color: "#fff", fontSize: 12, padding: "4px 28px 4px 10px",
                  appearance: "none", cursor: "pointer", outline: "none",
                  fontFamily: "inherit",
                }}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="INR">INR</option>
              </select>
              <span style={{ position: "absolute", right: 8, pointerEvents: "none" }}>
                <ChevronDownIcon />
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "#22243a" }} />

        {/* Footer — hide if already connected */}
        {accountStatus !== "complete" && (
        <div style={{ padding: "14px 20px", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10 }}>
          <button
            onClick={closeModal}
            style={{
              background: "none", border: "none", color: "#9aa0b4", fontSize: 13,
              cursor: "pointer", padding: "8px 14px", borderRadius: 6, fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
          <button
            onClick={openModal}
            style={{
              background: "#4f6ef7", border: "none", color: "#fff", fontSize: 13, fontWeight: 600,
              cursor: "pointer", padding: "9px 18px", borderRadius: 7, fontFamily: "inherit",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            Connect with Stripe <ArrowRightIcon />
          </button>
        </div>
        )}
      </div>

      {/* Bottom note */}
      <div style={{ fontSize: 12, color: "#6b7280", textAlign: "center",marginLeft: 165,  marginTop: 12, maxWidth: 680 }}>
        Don't have a{" "}
        <a href="#" style={{ color: "#635bff", textDecoration: "none" }}>Stripe account</a>?
        {" "}You'll be able to create one during the connection process.
      </div>

      {/* Help box */}
      <div style={{
        ...s.card({ marginTop: 12 }),
        padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%", border: "1.5px solid #22243a",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#6b7280", fontSize: 13, fontWeight: 600, flexShrink: 0,
          }}>?</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#cdd1e0", marginBottom: 2 }}>Questions?</div>
            <div style={{ fontSize: 11.5, color: "#6b7280" }}>Learn more about payments, payouts, and refunds in our Help Center.</div>
          </div>
        </div>
        <button style={{
          background: "none", border: "1px solid #2a2c3e", color: "#cdd1e0",
          fontSize: 12, fontWeight: 500, padding: "7px 14px", borderRadius: 7,
          cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
        }}>
          Visit Help Center <ExternalLinkIcon />
        </button>
      </div>

      {/* ── Modal ── */}
      {modalState !== "idle" && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100,
          }}
        >
          <div style={{
            background: "#12131c", border: "1px solid #22243a", borderRadius: 12,
            padding: 28, width: 360, maxWidth: "90vw",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          }}>

            {/* Confirm */}
            {modalState === "confirm" && (
              <>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginBottom: 6 }}>Connect with Stripe</div>
                <p style={{ fontSize: 12.5, color: "#7c8298", marginBottom: 20, lineHeight: 1.6 }}>
                  You'll be securely redirected to Stripe to link your account. We never store your login details or banking information.
                </p>
                <div style={{
                  background: "#1a1b28", border: "1px solid #22243a", borderRadius: 8,
                  padding: "12px 14px", marginBottom: 18, fontSize: 12, color: "#9aa0b4",
                  display: "flex", gap: 8, alignItems: "flex-start",
                }}>
                  <span style={{ color: "#4f6ef7", marginTop: 1, flexShrink: 0, display: "flex" }}><InfoIcon /></span>
                  Currency: <strong style={{ color: "#cdd1e0" }}>{currency}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  <button
                    onClick={closeModal}
                    style={{ background: "none", border: "none", color: "#9aa0b4", fontSize: 13, cursor: "pointer", padding: "8px 14px", borderRadius: 6, fontFamily: "inherit" }}
                  >Cancel</button>
                  <button
                    onClick={connectStripe}
                    style={{ background: "#4f6ef7", border: "none", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "9px 18px", borderRadius: 7, fontFamily: "inherit" }}
                  >Confirm &amp; Connect</button>
                </div>
              </>
            )}

            {/* Loading */}
            {modalState === "loading" && (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{
                  width: 30, height: 30, border: "3px solid #22243a", borderTopColor: "#4f6ef7",
                  borderRadius: "50%", margin: "0 auto 12px",
                  animation: "spin 0.8s linear infinite",
                }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <p style={{ fontSize: 13, color: "#7c8298" }}>Connecting to Stripe...</p>
              </div>
            )}

            {/* Success */}
            {modalState === "success" && (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}></div>
                <div style={{ fontWeight: 600, fontSize: 15, color: "#fff", marginBottom: 6 }}>Connected!</div>
                <p style={{ fontSize: 12.5, color: "#7c8298", marginBottom: 16 }}>Your Stripe account has been successfully linked.</p>
                <button
                  onClick={() => { closeModal(); }}
                  style={{ background: "#4f6ef7", border: "none", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "9px 24px", borderRadius: 7, fontFamily: "inherit" }}
                >Done</button>
              </div>
            )}

            {/* Error */}
            {modalState === "error" && (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}></div>
                <div style={{ fontWeight: 600, fontSize: 15, color: "#fff", marginBottom: 6 }}>Connection Failed</div>
                <p style={{ fontSize: 12.5, color: "#7c8298", marginBottom: 16 }}>{errorMsg}</p>
                <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
                  <button onClick={closeModal} style={{ background: "none", border: "1px solid #22243a", color: "#9aa0b4", fontSize: 13, cursor: "pointer", padding: "8px 16px", borderRadius: 7, fontFamily: "inherit" }}>
                    Cancel
                  </button>
                  <button onClick={connectStripe} style={{ background: "#4f6ef7", border: "none", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "9px 18px", borderRadius: 7, fontFamily: "inherit" }}>
                    Retry
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
}